import { useState, useEffect, useRef, useCallback } from "react";
import styles from "./Style.module.css";
import DropDownIcon from "../../Icons/DropDownIcon";
import SearchBar from "../../Search/SearchBar";
import CloseCircleIcon from "../../Icons/CloseCircleIcon";
import { highlightQuery } from "../../../Utils/utils";

export const BELOW_12_TAG_ID = "__below_12__";
export const BELOW_12_TAG_OPTION = {
	id: BELOW_12_TAG_ID,
	name: "Below 12",
	first_name: "Below 12",
	last_name: "",
	below_12: 1,
	is_below_12: true,
};
export const CLOSE_TAGGING_DROPDOWNS = "closeTaggingDropdowns";

export const isBelow12Tag = (item) => {
	if (!item) return false;
	return (
		item.id === BELOW_12_TAG_ID ||
		item.below_12 === 1 ||
		item.below_12 === "1" ||
		item.is_below_12 === true
	);
};

const TaggingDropdown = ({
	options = [],
	onSelect,
	selectedLabel,
	selectedOption: externalSelectedOption,
	onDropdownToggle = null,
	placeholder = "Search name",
	className = "",
	guestCheckinRemoteList = false,
	onGuestCheckinSearchQueryChange = null,
	onGuestCheckinLoadMore = null,
	guestCheckinHasMore = false,
	guestCheckinLoadingMore = false,
}) => {
	const [selectedOption, setSelectedOption] = useState({});
	const [isDropdownOpen, setDropdownOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const dropdownRef = useRef(null);
	const dropdownMenuRef = useRef(null);
	const searchInputRef = useRef(null);
	const loadMoreObserverRef = useRef(null);
	const loadMoreGateRef = useRef({
		hasMore: false,
		loadingMore: false,
		onLoadMore: null,
	});
	// Use external selectedOption if provided, otherwise use internal state
	const currentSelectedOption = externalSelectedOption || selectedOption;

	useEffect(() => {
		loadMoreGateRef.current = {
			hasMore: guestCheckinHasMore,
			loadingMore: guestCheckinLoadingMore,
			onLoadMore: onGuestCheckinLoadMore,
		};
	}, [guestCheckinHasMore, guestCheckinLoadingMore, onGuestCheckinLoadMore]);

	useEffect(() => {
		return () => {
			if (loadMoreObserverRef.current) {
				loadMoreObserverRef.current.disconnect();
				loadMoreObserverRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		const handleCloseDropdown = () => setDropdownOpen(false);
		window.addEventListener(CLOSE_TAGGING_DROPDOWNS, handleCloseDropdown);
		return () => {
			window.removeEventListener(CLOSE_TAGGING_DROPDOWNS, handleCloseDropdown);
		};
	}, []);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target)
			) {
				setDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// Auto-scroll dropdown into view inside scrollable parent
	useEffect(() => {
		if (isDropdownOpen && dropdownMenuRef.current) {
			dropdownMenuRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [isDropdownOpen]);

	// Focus search input when dropdown opens
	useEffect(() => {
		if (isDropdownOpen && searchInputRef.current) {
			// Small delay to ensure the dropdown is rendered
			setTimeout(() => {
				searchInputRef.current?.focus();
				searchInputRef.current?.select();
			}, 0);
		}
	}, [isDropdownOpen]);

	const handleSelect = (item) => {
		const selectedOptionData = { item: item };
		setSelectedOption(selectedOptionData);
		if (onSelect) {
			onSelect(item);
		}
		setDropdownOpen(false);
		setSearchQuery("");
	};

	const handleClearSelection = (e) => {
		e.stopPropagation(); // Prevent dropdown from opening
		setSelectedOption({});
		if (onSelect) {
			onSelect(null);
		}
	};

	// Format check-in date (e.g., "Tue, Aug 8")
	const formatCheckinDate = (dateString) => {
		if (!dateString) return "-";
		try {
			// Handle format like "2025-12-12 12:54:15" by replacing space with T
			let normalizedDate = dateString;
			if (typeof dateString === 'string' && dateString.includes(' ') && !dateString.includes('T')) {
				normalizedDate = dateString.replace(' ', 'T');
			}
			
			const date = new Date(normalizedDate);
			if (isNaN(date.getTime())) return "-";
			
			const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
			const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
			
			const dayName = days[date.getDay()];
			const month = months[date.getMonth()];
			const day = date.getDate();
			
			return `${dayName}, ${month} ${day}`;
		} catch (error) {
			return "-";
		}
	};

	// Format check-in time (e.g., "10:30 AM")
	const formatCheckinTime = (dateString) => {
		if (!dateString) return "-";
		try {
			// Handle format like "2025-12-12 12:54:15" by replacing space with T
			let normalizedDate = dateString;
			if (typeof dateString === 'string' && dateString.includes(' ') && !dateString.includes('T')) {
				normalizedDate = dateString.replace(' ', 'T');
			}
			
			const date = new Date(normalizedDate);
			if (isNaN(date.getTime())) return "-";
			
			const hours = date.getHours();
			const minutes = date.getMinutes();
			const ampm = hours >= 12 ? "PM" : "AM";
			const formattedHour = hours % 12 === 0 ? 12 : hours % 12;
			const formattedMin = minutes.toString().padStart(2, "0");
			
			return `${formattedHour}:${formattedMin} ${ampm}`;
		} catch (error) {
			return "-";
		}
	};

	// Get full name from user object
	const getFullName = (user) => {
		if (isBelow12Tag(user)) return "Below 12";
		const firstName = user?.first_name || user?.name || "";
		const lastName = user?.last_name || user?.lastname || "";
		return `${firstName} ${lastName}`.trim() || "-";
	};

	const highlightMatch = (raw) => {
		const text = raw == null || raw === "" ? "-" : String(raw);
		return highlightQuery(text, searchQuery.trim());
	};

	// Check if an item is a check-in user (has checkin user properties)
	const isCheckinUser = (item) => {
		if (!item) return false;
		// Check if it has checkin user properties (first_name, name, checkin, etc.)
		return !!(item.first_name || item.name) && !item.item_name;
	};

	// Ensure options is always an array
	const safeOptions = Array.isArray(options) ? options : [];

	// Check if options is a flat array (check-in users) or nested structure (rooms/items)
	const isCheckinList =
		guestCheckinRemoteList ||
		(safeOptions.length > 0 && safeOptions[0]?.checkin !== undefined);

	// Filter check-in users based on search query (client-side; skipped for server-driven list)
	const filteredCheckinUsers =
		isCheckinList && !guestCheckinRemoteList
			? safeOptions.filter((user) => {
					if (!searchQuery.trim()) return true;
					const query = searchQuery.toLowerCase();
					const fullName = getFullName(user).toLowerCase();
					return fullName.includes(query);
				})
			: [];

	const displayCheckinUsers = guestCheckinRemoteList ? safeOptions : filteredCheckinUsers;
	const displayCheckinOptions = isCheckinList
		? [
				BELOW_12_TAG_OPTION,
				...displayCheckinUsers.filter((user) => !isBelow12Tag(user)),
			]
		: [];

	const attachLoadMoreSentinel = useCallback(
		(node) => {
			if (loadMoreObserverRef.current) {
				loadMoreObserverRef.current.disconnect();
				loadMoreObserverRef.current = null;
			}
			if (
				!guestCheckinRemoteList ||
				!isCheckinList ||
				!onGuestCheckinLoadMore ||
				!guestCheckinHasMore ||
				!node
			) {
				return;
			}
			const root = dropdownMenuRef.current;
			loadMoreObserverRef.current = new IntersectionObserver(
				(entries) => {
					if (!entries[0]?.isIntersecting) return;
					const g = loadMoreGateRef.current;
					if (g.hasMore && !g.loadingMore) {
						g.onLoadMore?.();
					}
				},
				{ root: root || null, rootMargin: "80px", threshold: 0.01 }
			);
			loadMoreObserverRef.current.observe(node);
		},
		[
			guestCheckinRemoteList,
			isCheckinList,
			onGuestCheckinLoadMore,
			guestCheckinHasMore,
		]
	);

	// Filter options based on search query (for nested structure)
	const filteredOptions = !isCheckinList
		? safeOptions
				.map((option) => {
					// Ensure items is always an array
					const optionItems = Array.isArray(option?.items) ? option.items : [];
					
					// Filter items within each room
					const filteredItems = optionItems.filter((item) =>
						item?.item_name?.toLowerCase().includes(searchQuery.toLowerCase())
					);

					// Check if room name matches search query
					const roomNameMatches = option?.room_name?.toLowerCase().includes(searchQuery.toLowerCase());

					// Return room if room name matches OR if it has matching items
					if (roomNameMatches || filteredItems.length > 0) {
						return {
							...option,
							items: roomNameMatches ? optionItems : filteredItems
						};
					}
					return null;
				})
				.filter(Boolean) // Remove null values
		: [];

	return (
		<div
			ref={dropdownRef}
			className={`${styles.taggingDropdown} ${className}`}
			tabIndex={0}>
			<div className={`${styles.taggingDropdownToggle} dropdownToggle`}
				onClick={() => {
					const newState = !isDropdownOpen;
					setDropdownOpen(newState);
					if (onDropdownToggle) {
						onDropdownToggle(newState, searchQuery);
					}
				}}>
				{currentSelectedOption && currentSelectedOption.item ? (
					<div className={styles.selectedTag}>
						<span className={styles.selectedTagText}>
							{isCheckinList || isCheckinUser(currentSelectedOption.item)
								? getFullName(currentSelectedOption.item)
								: currentSelectedOption.item.item_name}
						</span>
						<button
							type="button"
							className={styles.removeTagButton}
							onClick={handleClearSelection}
							aria-label="Remove tag"
						>
							<CloseCircleIcon 
								size={16}
								color="#ffffff"
								crossColor="#3C2212"
							/>
						</button>
					</div>
				) : (
					<span className={styles.taggingDropdownToggleText}>
						{selectedLabel || 'Not Tagged'}
					</span>
				)}
				<DropDownIcon 
					size={12} 
					color="#6b7280" 
					className={`${styles.dropdownIcon} ${isDropdownOpen ? styles.rotated : ''}`}
				/>
			</div>
			{isDropdownOpen && (
				<div className={`${styles.dropdownContainer}`} ref={dropdownMenuRef}>
					{/* Search Bar */}
					<div className={styles.header}>
						<SearchBar
							ref={searchInputRef}
							placeholder={placeholder}
							value={searchQuery}
							onChange={(e) => {
								const v = e.target.value;
								setSearchQuery(v);
								if (
									guestCheckinRemoteList &&
									isCheckinList &&
									onGuestCheckinSearchQueryChange
								) {
									onGuestCheckinSearchQueryChange(v);
								}
							}}
							className="border-0"
						/>
					</div>
					<div className={`${styles.dropdownMenu} ${isCheckinList ? styles.checkinList : ''}`}>
						{isCheckinList ? (
							// Render check-in list
							displayCheckinOptions.length > 0 ? (
								<>
									{displayCheckinOptions.map((user, index) => (
										<div
											key={`checkin-${user?.id || user?.name}-${index}`}
											className={`
											${styles.checkinItem} 
										${isBelow12Tag(user) ? styles.below12CheckinItem : ''}
										${(isBelow12Tag(currentSelectedOption?.item) && isBelow12Tag(user)) || currentSelectedOption?.item?.id === user?.id ? styles.selectedCheckinItem : ''}
										${user?.has_no_booking ? styles.fullyBookedCheckinItem : ''}
										`}
											onClick={() => !user?.has_no_booking && handleSelect(user)}
											title={user?.has_no_booking ? "Tagged to another booking" : ""}
										>
											{isBelow12Tag(user) ? (
												<div className={styles.checkinItemContent}>
													<div className={styles.checkinField}>
														<span className={styles.checkinLabel}>Option:</span>
														<span className={styles.checkinValue}>Below 12</span>
													</div>
												</div>
											) : (
												<div className={styles.checkinItemContent}>
													<div className={styles.checkinField}>
														<span className={styles.checkinLabel}>Full Name:</span>
														<span className={styles.checkinValue}>{highlightMatch(getFullName(user))}</span>
													</div>
													<div className={styles.checkinField}>
														<span className={styles.checkinLabel}>Nationality:</span>
														<span className={styles.checkinValue}>{highlightMatch(user?.nationality)}</span>
													</div>
													<div className={styles.checkinField}>
														<span className={styles.checkinLabel}>Mobile Number:</span>
														<span className={styles.checkinValue}>{highlightMatch(user?.contact_number)}</span>
													</div>
													<div className={styles.checkinField}>
														<span className={styles.checkinLabel}>NRIC or Passport No.:</span>
														<span className={styles.checkinValue}>{highlightMatch(user?.passport_number)}</span>
													</div>
													<div className={styles.checkinField}>
														<span className={styles.checkinLabel}>Check-in Date:</span>
														<span className={styles.checkinValue}>{highlightMatch(formatCheckinDate(user?.checkin))}</span>
													</div>
													<div className={styles.checkinField}>
														<span className={styles.checkinLabel}>Time-in:</span>
														<span className={styles.checkinValue}>{highlightMatch(formatCheckinTime(user?.checkin))}</span>
													</div>
												</div>
											)}
										</div>
									))}
									{guestCheckinRemoteList &&
										onGuestCheckinLoadMore &&
										guestCheckinHasMore && (
											<div
												ref={attachLoadMoreSentinel}
												style={{ height: 1, width: "100%" }}
												aria-hidden
											/>
										)}
									{guestCheckinRemoteList && guestCheckinLoadingMore && (
										<div
											className={styles.noResults}
											style={{
												color: "#666",
												padding: "12px",
												textAlign: "center",
												fontSize: 13,
											}}
										>
											Loading more…
										</div>
									)}
								</>
							) : (
								<div className={styles.noResults} style={{ color: "#999", fontStyle: "italic", padding: "20px", textAlign: "center" }}>
									No results found
								</div>
							)
						) : (
							// Render nested structure (rooms/items)
							filteredOptions.length > 0 ? (
								filteredOptions.map((option, index) => {
									const optionItems = Array.isArray(option?.items) ? option.items : [];
									const availableItems = optionItems.filter(item => item?.bookings?.length === 0)?.length || 0;
									return (
										<div
											key={index}
											className={`${styles.dropdownItem}`}
										>
											<div className={styles.topHeading}>
												<h6>{option?.room_name ? option?.room_name : 'N/A'}</h6>
												{availableItems > 0 ? (
													<div className={`${styles.badge} ${styles.available}`}>
														Available: {availableItems}
													</div>
												) : (
													<div className={`${styles.badge} ${styles.fullyBooked}`}>Fully Booked</div>
												)}
											</div>
											<div className={styles.bottomList}>
												{Array.isArray(option?.items) && option.items.length > 0 && option.items.map((item, index) => (
													<div className={styles.item} 
														key={index}
														onClick={() => handleSelect(item)}
													>
														<div className={styles.left}>
															<span className={styles.checkMark}></span>
															<p>{item?.item_name}</p>
														</div>
														<div className={styles.right}>
															{item?.bookings?.length === 0 ? "" : "Unavailable"}
														</div>
													</div>
												)
												)}
											</div>
										</div>
									);
								})
							) : (
								<div className="dropdownItem" style={{ color: "#999", fontStyle: "italic" }}>
									No results found
								</div>
							)
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default TaggingDropdown;
